import { z } from "zod";

export const COMMAND_SCHEMA_VERSION = 1;

export const commandActorTypes = [
  "human",
  "system",
  "worker",
  "integration",
  "codex",
  "test",
] as const;
export type CommandActorType = (typeof commandActorTypes)[number];

export const commandOrigins = [
  "ui",
  "api",
  "worker",
  "integration",
  "system",
  "test",
] as const;
export type CommandOrigin = (typeof commandOrigins)[number];

export const taskOrigins = ["human", "system", "automation", "integration", "codex"] as const;
export type TaskOrigin = (typeof taskOrigins)[number];

export const taskActionTypes = [
  "manual_follow_up",
  "human_task",
  "review",
  "sensitive_action",
] as const;
export type TaskActionType = (typeof taskActionTypes)[number];

export interface ImmutableActionReference {
  type: string;
  id: string;
  version: number;
  runId?: string;
  runVersion?: number;
  stepId?: string;
  stepVersion?: number;
  blocking?: boolean;
}

export interface CommandContext {
  ownerId: string;
  actorType: CommandActorType;
  actorId: string;
  origin: CommandOrigin;
  commandId: string;
  correlationId: string;
  causationId?: string;
  idempotencyKey: string;
  requestedAt: string;
  expectedVersion?: number;
  schemaVersion: number;
}

export const commandContextSchema = z.object({
  ownerId: z.string().trim().min(1),
  actorType: z.enum(commandActorTypes),
  actorId: z.string().trim().min(1),
  origin: z.enum(commandOrigins),
  commandId: z.string().trim().min(1),
  correlationId: z.string().trim().min(1),
  causationId: z.string().trim().min(1).optional(),
  idempotencyKey: z.string().trim().min(1),
  requestedAt: z.string().datetime({ offset: true }),
  expectedVersion: z.number().int().positive().optional(),
  schemaVersion: z.number().int().positive(),
});

export type CommandContextInput = Omit<
  CommandContext,
  "commandId" | "correlationId" | "requestedAt" | "schemaVersion"
> &
  Partial<Pick<CommandContext, "commandId" | "correlationId" | "requestedAt" | "schemaVersion">>;

type CommandContextDeps = {
  now?: () => string;
  uuid?: () => string;
};

export function createCommandContext(
  input: CommandContextInput,
  deps: CommandContextDeps = {},
): CommandContext {
  const uuid = deps.uuid ?? (() => crypto.randomUUID());
  const context = {
    ...input,
    commandId: input.commandId ?? uuid(),
    correlationId: input.correlationId ?? uuid(),
    requestedAt: input.requestedAt ?? (deps.now ?? (() => new Date().toISOString()))(),
    schemaVersion: input.schemaVersion ?? COMMAND_SCHEMA_VERSION,
  };
  return commandContextSchema.parse(context);
}

export type CommandErrorCode =
  | "validation_error"
  | "unauthenticated"
  | "forbidden"
  | "not_found"
  | "invalid_state"
  | "version_conflict"
  | "idempotency_conflict"
  | "persistence_error"
  | "provider_timeout"
  | "provider_transient"
  | "provider_permanent"
  | "provider_rate_limited"
  | "provider_revoked"
  | "ambiguous_external_result"
  | "lease_expired"
  | "dead_letter";

export interface CommandError {
  code: CommandErrorCode;
  message: string;
  retryable: boolean;
  correlationId: string;
  severity: "info" | "warning" | "error" | "critical";
  sanitizedCause?: string;
  details?: Record<string, unknown>;
}

export interface CommandSuccess<T> {
  ok: true;
  status: "applied" | "replayed";
  value: T;
  commandId: string;
  correlationId: string;
  version: number;
  eventIds: string[];
  outboxJobIds: string[];
  auditId: string;
  warnings: string[];
}

export interface CommandFailure {
  ok: false;
  status: "rejected" | "conflict" | "failed";
  error: CommandError;
  commandId: string;
  correlationId: string;
  version?: number;
  eventIds: string[];
  outboxJobIds: string[];
  auditId?: string;
  warnings: string[];
}

export type CommandResult<T> = CommandSuccess<T> | CommandFailure;

export function commandError(
  context: CommandContext,
  code: CommandErrorCode,
  message: string,
  options: Partial<Pick<CommandError, "retryable" | "severity" | "sanitizedCause" | "details">> = {},
): CommandFailure {
  const conflict = code === "idempotency_conflict" || code === "version_conflict";
  return {
    ok: false,
    status: conflict ? "conflict" : code === "validation_error" || code === "invalid_state" ? "rejected" : "failed",
    error: {
      code,
      message,
      retryable: options.retryable ?? false,
      correlationId: context.correlationId,
      severity: options.severity ?? (code === "validation_error" || code === "invalid_state" ? "warning" : "error"),
      sanitizedCause: options.sanitizedCause,
      details: options.details,
    },
    commandId: context.commandId,
    correlationId: context.correlationId,
    eventIds: [],
    outboxJobIds: [],
    warnings: [],
  };
}

export function isCommandSuccess<T>(result: CommandResult<T>): result is CommandSuccess<T> {
  return result.ok;
}

function stableSerialize(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(",")}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, item]) => `${JSON.stringify(key)}:${stableSerialize(item)}`);
  return `{${entries.join(",")}}`;
}

export function stableFingerprint(value: unknown): string {
  return stableSerialize(value);
}

export type AuditResult = "applied" | "replayed" | "rejected" | "failed";

export interface AuditEntry {
  id: string;
  ownerId: string;
  command: string;
  commandId: string;
  correlationId: string;
  causationId?: string;
  actorType: CommandActorType;
  actorId: string;
  origin: CommandOrigin;
  targetType: string;
  targetId: string;
  idempotencyKey: string;
  requestFingerprint: string;
  result: AuditResult;
  occurredAt: string;
  expectedVersion?: number;
  previousVersion?: number;
  nextVersion?: number;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  errorCode?: CommandErrorCode;
}

export interface CommandReceipt {
  id: string;
  ownerId: string;
  command: string;
  idempotencyKey: string;
  requestFingerprint: string;
  commandId: string;
  correlationId: string;
  result: CommandResult<unknown>;
  createdAt: string;
  schemaVersion: number;
}

export type ExternalProvider = string;
export type ConnectionStatus = "pending" | "connected" | "degraded" | "revoked" | "disconnected";
export const externalSyncStates = ["disconnected", "pending", "synced", "error", "revoked"] as const;
export type ExternalSyncState = (typeof externalSyncStates)[number];

export interface ExternalConnection {
  id: string;
  ownerId: string;
  provider: ExternalProvider;
  accountRef: string;
  status: ConnectionStatus;
  capabilities: string[];
  secretRef?: string;
  connectedAt?: string;
  lastCheckedAt?: string;
  lastSyncedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export type OutboxJobStatus =
  | "pending"
  | "leased"
  | "running"
  | "retry_scheduled"
  | "reconciling"
  | "succeeded"
  | "dead_letter"
  | "cancelled";

export interface OutboxJob {
  id: string;
  ownerId: string;
  kind: string;
  payloadVersion: number;
  payload: Record<string, unknown>;
  idempotencyKey: string;
  commandId: string;
  correlationId: string;
  causationId?: string;
  status: OutboxJobStatus;
  scheduledAt: string;
  leaseOwner?: string;
  leaseToken?: string;
  leaseExpiresAt?: string;
  attempts: number;
  maxAttempts: number;
  nextAttemptAt?: string;
  lastAttemptAt?: string;
  lastErrorCode?: CommandErrorCode;
  createdAt: string;
  updatedAt: string;
}

export type AutomationJobState =
  | "pending"
  | "leased"
  | "running"
  | "retry_scheduled"
  | "reconciling"
  | "succeeded"
  | "dead_letter"
  | "cancelled";

export interface AutomationJob {
  id: string;
  ownerId: string;
  outboxJobId?: string;
  kind: string;
  payloadVersion: number;
  payload: Record<string, unknown>;
  idempotencyKey: string;
  intentFingerprint?: string;
  state: AutomationJobState;
  scheduledAt: string;
  leaseOwner?: string;
  leaseToken?: string;
  leaseExpiresAt?: string;
  attempts: number;
  maxAttempts: number;
  nextAttemptAt?: string;
  lastAttemptAt?: string;
  completedAt?: string;
  lastErrorCode?: CommandErrorCode;
  createdAt: string;
  updatedAt: string;
  correlationId: string;
}

export type JobAttemptStatus = "started" | "succeeded" | "retry_scheduled" | "reconciling" | "failed" | "expired";

export interface JobAttempt {
  id: string;
  ownerId: string;
  jobId: string;
  attempt: number;
  status: JobAttemptStatus;
  leaseToken: string;
  startedAt: string;
  finishedAt?: string;
  providerRequestId?: string;
  providerResponseHash?: string;
  errorCode?: CommandErrorCode;
  retryable?: boolean;
  sanitizedCause?: string;
}

export type IncidentSeverity = "info" | "warning" | "error" | "critical";
export type IncidentStatus = "open" | "acknowledged" | "resolved";

export interface AutomationIncident {
  id: string;
  ownerId: string;
  code: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  sanitizedCause: string;
  scope: string;
  impact: string;
  retryable: boolean;
  attemptCount: number;
  nextRetryAt?: string;
  jobId?: string;
  connectionId?: string;
  correlationId: string;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt?: string;
  resolution?: string;
}

export interface FeatureFlag {
  key: string;
  ownerId: string;
  enabled: boolean;
  readEnabled: boolean;
  writeEnabled: boolean;
  enrollmentEnabled: boolean;
  executionEnabled: boolean;
  reason: string;
  updatedAt: string;
}

export function disabledFeatureFlag(
  key: string,
  ownerId: string,
  reason: string,
  updatedAt: string,
): FeatureFlag {
  return {
    key,
    ownerId,
    enabled: false,
    readEnabled: false,
    writeEnabled: false,
    enrollmentEnabled: false,
    executionEnabled: false,
    reason,
    updatedAt,
  };
}
